import {
  AvailableCredit,
  EditWorksheetDto,
  FilteredCreditApplication,
  FilteredWorksheet,
  FinancingConfigDto,
  Loader,
  useUpdateWorksheet,
} from '@applyonline/shared'
import { ProblemOccuredAlert, ScrollableAlert, Stepper, StepperStep } from '@applyonline/shared/components'
import { yupResolver } from '@hookform/resolvers/yup'
import { compareAsc } from '@if/core/date'
import { EFinancingProgram, EPaymentFrequency, EWorksheetStatus } from '@if/core/types'
import { formatDate } from '@if/core/utils'
import { AxiosError, HttpStatusCode } from 'axios'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import useTermPaymentsTable from '../InitialApprobationPage/hooks'
import FinalAmountStep from './components/FinalAmountStep'
import FirstPaymentStep from './components/FirstPaymentStep'
import InsuranceStep from './components/InsuranceStep'
import SummaryStep from './components/SummaryStep'
import TermStep from './components/TermStep'
import { EWorksheetStep, useWorksheetStepIsValid } from './hooks'
import { WorksheetForm, useWorksheetSchema } from './schema-hooks'

interface Props {
  step?: EWorksheetStep
  setStep: (step: EWorksheetStep) => void
  creditApp: FilteredCreditApplication
  worksheet: FilteredWorksheet
  financingProgramConfig: FinancingConfigDto
  availableCredit: AvailableCredit | undefined
}

const WorksheetStepper = ({
  creditApp,
  step = EWorksheetStep.AmountRequested,
  setStep,
  worksheet,
  financingProgramConfig,
  availableCredit,
}: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [error, setError] = useState<string>('')
  const [showProblemOccured, setShowProblemOccured] = useState<boolean>(false)
  const [submitWorksheetUpdate, isSubmitting] = useUpdateWorksheet(EFinancingProgram.B2c)
  const currentDate = useMemo(() => new Date(), [])

  const availableCreditAmount = availableCredit?.availableAmount ?? creditApp.finalDecision.maxAmountFinanced
  const amountRequestedDefaultValue = Math.min(
    worksheet.amountRequested,
    creditApp.finalDecision.maxAmountFinanced,
    availableCreditAmount,
  )

  const schema = useWorksheetSchema(
    availableCreditAmount,
    amountRequestedDefaultValue,
    worksheet.term,
    financingProgramConfig,
  )

  const form = useForm<WorksheetForm>({
    disabled: isSubmitting,
    mode: 'onChange',
    defaultValues: {
      firstPaymentOn: formatDate(worksheet.firstPaymentOn) || undefined,
      amountRequested: amountRequestedDefaultValue,
      term: worksheet.term,
      includeInsurance: worksheet.includeInsurance,
    },
    resolver: yupResolver(schema),
  })

  const firstPaymentOn = form.watch('firstPaymentOn')
  const amountRequested = form.watch('amountRequested')
  const [amountRequestedDebounced, setAmountRequestedDebounced] = useState(amountRequested)

  const handleSetStep = useCallback(
    (s: string) => {
      setStep(s as EWorksheetStep)
      setAmountRequestedDebounced(amountRequested)
    },
    [setStep, setAmountRequestedDebounced, amountRequested],
  )

  const [monthlyPaymentPerMonthsIncludingInsurance, isLoadingTerms] = useTermPaymentsTable(creditApp, {
    firstPaymentOn,
    activationDate: currentDate,
    requestedAmount: Number(amountRequestedDebounced),
    includeInsurance: true,
  })

  const term = form.watch('term')

  const selectedTermCanIncludeProtectionPlan =
    monthlyPaymentPerMonthsIncludingInsurance.find((mp) => mp.term === term) !== undefined
  const thereExistsAValidTermIncludingProtectionPlan = monthlyPaymentPerMonthsIncludingInsurance.length > 0
  const applicantIsYoungEnoughToHaveProtectionPlan = creditApp.applicant.age <= 65
  const mightBeAbleToHaveProtectionPlan =
    thereExistsAValidTermIncludingProtectionPlan && applicantIsYoungEnoughToHaveProtectionPlan

  const steps = useMemo<EWorksheetStep[]>(() => {
    const stepList = []
    stepList.push(EWorksheetStep.AmountRequested, EWorksheetStep.FirstPayment, EWorksheetStep.PayOverTime)
    if (mightBeAbleToHaveProtectionPlan) stepList.push(EWorksheetStep.ProtectionPlan)
    stepList.push(EWorksheetStep.Summary)
    return stepList
  }, [mightBeAbleToHaveProtectionPlan])

  // Anti skip step
  useWorksheetStepIsValid(step, steps, form.trigger)
  // Additionnal step validation
  useEffect(() => {
    // If the choice of first payment date and term make it so it is no longer possible
    // to offer a protection plan, skip ahead of protection plan to summary.
    if (!selectedTermCanIncludeProtectionPlan && step === EWorksheetStep.ProtectionPlan) {
      form.setValue('includeInsurance', false)
      navigate(`../worksheet/${EWorksheetStep.Summary}`, { replace: true })
    }
  }, [selectedTermCanIncludeProtectionPlan, navigate, step, form])

  const onFormSubmitted = useCallback(
    (data: WorksheetForm) => {
      if (!currentDate) {
        setError(t('worksheet.error.unavailableDelivery'))
        return
      }
      const nowUTC = new Date(`${currentDate.toLocaleDateString()}T00:00:00`)
      const expiry = new Date(creditApp.expiresOn)
      const receivedOnPlus30Days = new Date(creditApp.hardHitReportReceivedOn)
      receivedOnPlus30Days.setDate(receivedOnPlus30Days.getDate() + 30)
      if (data.amountRequested > (availableCredit?.availableAmount ?? 0)) {
        setError(t('worksheet.error.amountRequestedHigherThanAvailableCredit'))
      } else if (compareAsc(currentDate, receivedOnPlus30Days) === 1)
        setError(t('worksheet.error.activationDateReport30Days'))
      else if (compareAsc(nowUTC, expiry) === 1) setError(t('worksheet.error.deliveryOnEarlierExpiration'))
      else {
        const ws: EditWorksheetDto = {
          ...worksheet,
          amountRequested: Number(data.amountRequested),
          term: data.term,
          firstPaymentOn: data.firstPaymentOn,
          includeInsurance: data.includeInsurance,
          paymentFrequency: EPaymentFrequency.Monthly,
          deliveryOn: formatDate(currentDate),
          status: EWorksheetStatus.Active,
          creditApplicationId: creditApp.id,
          create: false,
          versionTag: worksheet.versionTag,
        }
        submitWorksheetUpdate(ws)
          .then(() => {
            navigate(`/creditApplication/${creditApp.id}/contract`)
          })
          .catch((err: AxiosError) => {
            if (err.response?.status === HttpStatusCode.Conflict) setError(t('common.conflict'))
            else setShowProblemOccured(true)
          })
      }
    },
    [
      currentDate,
      creditApp.expiresOn,
      creditApp.hardHitReportReceivedOn,
      creditApp.id,
      availableCredit?.availableAmount,
      t,
      worksheet,
      submitWorksheetUpdate,
      navigate,
    ],
  )

  if (isLoadingTerms)
    return (
      <Loader
        title={t('common.withYouShortly', { ns: 'shared' })}
        description={t('common.pleaseWait', { ns: 'shared' })}
      />
    )

  if (!availableCredit) {
    return (
      <main className="general-message">
        <div className="form-section" style={{ width: '100%' }}>
          <ProblemOccuredAlert showAlert={true} />
        </div>
      </main>
    )
  }

  return (
    <main className="general-message">
      <ScrollableAlert showAlert={!!error} type="warn" message={error} />
      <ProblemOccuredAlert showAlert={showProblemOccured} />
      <div className="form-section" style={{ width: '100%' }}>
        <FormProvider {...form}>
          <Stepper current={step} setCurrentStep={handleSetStep} isSubmitting={isSubmitting}>
            <StepperStep
              step={EWorksheetStep.AmountRequested}
              icon="hand-holding-dollar"
              label={t('worksheet.finalAmount.step')}
              render={({ setState }) => (
                <FinalAmountStep
                  setStepState={setState}
                  maxAmountFinanced={creditApp.finalDecision.maxAmountFinanced}
                  availableCredit={availableCredit}
                />
              )}
            />
            <StepperStep
              step={EWorksheetStep.FirstPayment}
              icon="calendar-day"
              label={t('worksheet.firstPayment.step')}
              hideControl
              render={({ setState, setNext }) => (
                <FirstPaymentStep
                  setStepState={setState}
                  setNext={setNext}
                  soonestDeliveryOn={currentDate}
                  creditAppId={creditApp.id}
                />
              )}
            />
            <StepperStep
              step={EWorksheetStep.PayOverTime}
              icon="percent"
              label={t('worksheet.term.step')}
              hideControl
              render={({ setState, setNext }) => (
                <TermStep
                  setStepState={setState}
                  setNext={setNext}
                  creditApp={creditApp}
                  soonestDeliveryOn={currentDate}
                />
              )}
            />
            {mightBeAbleToHaveProtectionPlan && (
              <StepperStep
                step={EWorksheetStep.ProtectionPlan}
                icon="shield"
                label={t('worksheet.protection.step')}
                hideControl
                render={({ setState, setNext }) => (
                  <InsuranceStep
                    setStepState={setState}
                    setNext={setNext}
                    monthlyPaymentPerMonths={monthlyPaymentPerMonthsIncludingInsurance}
                  />
                )}
              />
            )}
            <StepperStep
              step={EWorksheetStep.Summary}
              icon="memo-circle-check"
              label={t('worksheet.summary.step')}
              hideControl
              render={({ setState }) => (
                <SummaryStep
                  setStepState={setState}
                  setCurrentStep={handleSetStep}
                  creditApp={creditApp}
                  soonestDeliveryOn={currentDate}
                  onSubmit={form.handleSubmit(onFormSubmitted)}
                  isSubmitting={isSubmitting}
                />
              )}
            />
          </Stepper>
        </FormProvider>
      </div>
    </main>
  )
}

export default WorksheetStepper
